import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigContext } from '../components/shared/ConfigContext';
import { fetchMyEntitlements } from '../api/planClient';
import eventBus from '../utils/eventBus';

// "What may this company do right now" — loaded once per session from
// GET /me/entitlements and refreshed whenever the server says 402 or the
// active company changes. The UI only *reflects* entitlements; the server
// enforces them, so a stale snapshot can never grant anything.
const EntitlementsContext = createContext({
  status: 'idle', snapshot: null, plan: null, subscription: null, subscribed: true, ungated: true,
  can: () => true, usage: () => null, refresh: () => {},
});

export const EntitlementsProvider = ({ children }) => {
  const config = useContext(ConfigContext);
  const [status, setStatus] = useState('idle');
  const [snapshot, setSnapshot] = useState(null);

  const refresh = useCallback(() => {
    const role = localStorage.getItem('userRole');
    const hasSession = Boolean(localStorage.getItem('username'));
    if (!config?.backendUrl || !hasSession || role === 'global_admin' || !localStorage.getItem('companyId')) {
      setSnapshot(null);
      setStatus('idle');
      return;
    }
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    fetchMyEntitlements(config.backendUrl)
      .then((res) => {
        setSnapshot(res.data);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error loading entitlements', error);
        setStatus('error');
      });
  }, [config]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const offDenied = eventBus.on(eventBus.EVENTS.ENTITLEMENT_DENIED, refresh);
    const offRefresh = eventBus.on(eventBus.EVENTS.ENTITLEMENTS_REFRESH, refresh);
    return () => { offDenied(); offRefresh(); };
  }, [refresh]);

  const value = useMemo(() => {
    const features = snapshot?.features || {};
    const ungated = !snapshot || snapshot.ungated === true;
    return {
      status,
      snapshot,
      plan: snapshot?.plan || null,
      subscription: snapshot?.subscription || null,
      // No snapshot yet → assume subscribed; the server is the judge.
      subscribed: !snapshot || snapshot.subscribed !== false,
      periodEnd: snapshot?.periodEnd || null,
      ungated,
      // Unknown key or no snapshot yet → allowed; the server is the judge.
      can: (key) => ungated || !(key in features) || features[key].enabled !== false,
      usage: (key) => (features[key] ? features[key] : null),
      refresh,
    };
  }, [status, snapshot, refresh]);

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
};

export const useEntitlements = () => useContext(EntitlementsContext);

export const useFeature = (key) => {
  const { can, usage, status } = useEntitlements();
  return { allowed: can(key), usage: usage(key), status };
};

export default EntitlementsContext;
